import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { throwServerError } from '@apollo/client';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { DEFAULT_CHANNEL_CODE } from '@vendure/common/lib/shared-constants';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';
import { BehaviorSubject, combineLatest, EMPTY, Observable, Subject, Subscription } from 'rxjs';
import { debounce, debounceTime, filter, map, startWith, switchMap, take } from 'rxjs/operators';

import { CurrentUserChannel } from '../../common/generated-types';
import { DataService } from '../../data/providers/data.service';
import { LocalStorageService } from '../../providers/local-storage/local-storage.service';

@Component({
  selector: 'vdr-channel-switcher',
  templateUrl: './channel-switcher.component.html',
  styleUrls: ['./channel-switcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelSwitcherComponent implements OnInit, OnDestroy {
  readonly displayFilterThreshold = 10;
  channels$: Observable<CurrentUserChannel[]>;
  channelCount$: Observable<number>;
  filterControl = new FormControl('');
  activeChannelCode$: Observable<string>;
  activeChannelId!: string;
  public currentUrl!: string;
  public canShowDefaultChannel!: boolean;
  private changingChannel = false;
  private subscription$: Subscription = new Subscription();

  constructor(private dataService: DataService, private localStorageService: LocalStorageService, private router: Router) {}

  ngOnInit(): void {
    const channels$ = this.dataService.client
      .channels()
      .mapStream((data) => data.channels)
      .pipe(map((channels) => channels.filter((c) => c.customFields.deletedAt == null && !c.token.match(/^B/))));
    const filterTerm$ = this.filterControl.valueChanges.pipe<string>(startWith(''));
    this.channels$ = combineLatest(channels$, filterTerm$).pipe(
      map(([channels, filterTerm]) => {
        return filterTerm ? channels.filter((c) => c.code.toLocaleLowerCase().includes(filterTerm.toLocaleLowerCase())) : channels;
      }),
    );
    this.channelCount$ = channels$.pipe(map((channels) => channels.length));
    const activeChannel$ = this.dataService.client
      .userStatus()
      .mapStream((data) => data.userStatus.channels.find((c) => c.id === data.userStatus.activeChannelId))
      .pipe(filter(notNullOrUndefined));
    this.activeChannelCode$ = activeChannel$.pipe(
      debounceTime(300),
      map((channel) => {
        // if (channel.code == '__default_channel__') {
        //   this.channels$
        //     .pipe(
        //       map((channels) => {
        //         this.setActiveChannel(channels[0]?.id);
        //       }),
        //     )
        //     .subscribe();
        // }
        this.activeChannelId = channel.id;
        return channel.code;
      }),
    );
    this.checkIfDefaultChannelAvailable(this.router.url);
    this.subscription$.add(
      this.router.events
        .pipe(filter((event: any) => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => this.checkIfDefaultChannelAvailable(event.url)),
    );
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  private checkIfDefaultChannelAvailable(url: string): void {
    this.currentUrl = url;
    if (url.includes('/orders')) {
      this.canShowDefaultChannel = false;
      this.channels$
        .pipe(
          switchMap((d) => this.activeChannelCode$.pipe(map(() => d))),
          filter((value: CurrentUserChannel[]) => !!value),
          take(1),
        )
        .subscribe((channels: CurrentUserChannel[]) => {
          const defaultChannel = channels.find((channel: CurrentUserChannel) => channel.code == '__default_channel__');
          if (defaultChannel && this.activeChannelId == defaultChannel.id) {
            this.setActiveChannel(channels[1].id);
          }
        });
    } else {
      this.canShowDefaultChannel = true;
    }
  }

  public setActiveChannel(channelId: string) {
    if (this.changingChannel || this.activeChannelId === channelId) {
      return;
    }
    this.changingChannel = true;
    return this.dataService.client.setActiveChannel(channelId).subscribe(({ setActiveChannel }) => {
      const activeChannel = setActiveChannel.channels.find((c) => c.id === channelId);
      if (activeChannel) {
        this.localStorageService.set('activeChannelToken', activeChannel.token);
      }
      this.filterControl.patchValue('');
      this.changingChannel = false;
    });
  }
}
